import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TextField,
  Button,
  Banner,
  Spinner,
  Badge,
  Modal,
  Select,
  ButtonGroup,
  Icon,
  Pagination,
  Tabs,
  Checkbox,
  ProgressBar,
} from '@shopify/polaris';
import { PrintIcon, ExternalIcon, RefreshIcon, ViewIcon, XCircleIcon, ExportIcon } from '@shopify/polaris-icons';
import {
  fetchFulfillmentOrders,
  fetchShippingRates,
  createShipment,
  fetchLabelUrl,
  downloadLabel,
  fetchShipments,
  fetchShopStatus,
  fetchFulfillmentCounts,
  setShopDomain,
  getShopDomain,
  getShopifyOrderUrl,
  bulkCreateShipments,
  bulkFetchLabels,
  cancelShipment,
  exportShipmentsCsv,
  ApiError,
  type OrderWithFulfillmentOrders,
  type ShippingRate,
  type CreateShipmentResponse,
  type ShipmentListItem,
  type BulkShipResponse,
  type BulkLabelsResponse,
} from '../utils/api';

// Auto-refresh intervals (in milliseconds)
// Can be overridden via environment variables (values in seconds)
const DEFAULT_PENDING_REFRESH_SECONDS = 60;
const DEFAULT_SHIPPED_REFRESH_SECONDS = 180;

const PENDING_ORDERS_REFRESH_MS = (
  parseInt(import.meta.env.VITE_PENDING_ORDERS_REFRESH_INTERVAL, 10) || DEFAULT_PENDING_REFRESH_SECONDS
) * 1000;

const SHIPPED_ORDERS_REFRESH_MS = (
  parseInt(import.meta.env.VITE_SHIPPED_ORDERS_REFRESH_INTERVAL, 10) || DEFAULT_SHIPPED_REFRESH_SECONDS
) * 1000;

// Status helpers
function getStatusTone(status: string): 'success' | 'warning' | 'critical' | 'info' | 'attention' | undefined {
  switch (status) {
    case 'delivered': return 'success';
    case 'out_for_delivery':
    case 'in_transit':
    case 'picked_up': return 'info';
    case 'label_created': return 'attention';
    case 'attempted_delivery': return 'warning';
    case 'failed':
    case 'cancelled':
    case 'exception': return 'critical';
    default: return undefined;
  }
}

function formatStatus(status: string): string {
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Map Shopify fulfillment status to shipping-friendly labels
function formatShippingStatus(status: string): string {
  const statusMap: Record<string, string> = {
    'UNFULFILLED': 'Awaiting Shipment',
    'FULFILLED': 'Shipped',
    'PARTIALLY_FULFILLED': 'Partially Shipped',
    'IN_PROGRESS': 'Processing',
    'ON_HOLD': 'On Hold',
    'SCHEDULED': 'Scheduled',
  };
  return statusMap[status] || formatStatus(status);
}

// Format count for badge display
function formatBadgeCount(count: number): string {
  if (count === 0) return '';
  return count > 99 ? '99+' : String(count);
}

// Format last updated time for display
function formatLastUpdated(timestamp: number): string {
  if (!timestamp) return '';
  const now = Date.now();
  const diffSeconds = Math.floor((now - timestamp) / 1000);
  if (diffSeconds < 10) return 'Just now';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Main component with tabs
export default function FulfillmentsPage() {
  const [selectedTab, setSelectedTab] = useState(0);

  // Fetch shop status to cache shop domain for order links
  const { data: shopStatus } = useQuery({
    queryKey: ['shopStatus'],
    queryFn: fetchShopStatus,
    staleTime: Infinity, // Only fetch once per session
  });

  // Fetch counts for tab badges
  const { data: counts } = useQuery({
    queryKey: ['fulfillmentCounts'],
    queryFn: fetchFulfillmentCounts,
    refetchInterval: 60000, // Refresh every minute
  });

  // Cache shop domain when loaded
  if (shopStatus?.shopDomain) {
    setShopDomain(shopStatus.shopDomain);
  }

  const pendingBadge = formatBadgeCount(counts?.pendingCount || 0);
  const shippedBadge = formatBadgeCount(counts?.shippedCount || 0);

  const tabs = [
    {
      id: 'pending',
      content: 'Pending Orders',
      badge: pendingBadge || undefined,
      panelID: 'pending-panel',
    },
    {
      id: 'shipped',
      content: 'Shipped (7 days)',
      badge: shippedBadge || undefined,
      panelID: 'shipped-panel',
    },
  ];

  return (
    <div>
      <div className="jitsu-card">
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
      </div>
      {selectedTab === 0 && <PendingOrdersPanel />}
      {selectedTab === 1 && <ShippedOrdersPanel />}
    </div>
  );
}

// Page size options
const PAGE_SIZE_OPTIONS = [
  { label: '10 per page', value: '10' },
  { label: '25 per page', value: '25' },
  { label: '50 per page', value: '50' },
  { label: '100 per page', value: '100' },
];

// ============================================
// PENDING ORDERS PANEL
// ============================================
function PendingOrdersPanel() {
  const [searchValue, setSearchValue] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]); // For going back
  const [selectedOrder, setSelectedOrder] = useState<OrderWithFulfillmentOrders | null>(null);
  const [selectedRate, setSelectedRate] = useState<string>('');
  const [showRateModal, setShowRateModal] = useState(false);
  const [createdShipment, setCreatedShipment] = useState<CreateShipmentResponse | null>(null);
  const [labelLoading, setLabelLoading] = useState(false);
  const queryClient = useQueryClient();

  // Bulk selection state
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [bulkServiceLevel, setBulkServiceLevel] = useState('STANDARD');
  const [showBulkShipModal, setShowBulkShipModal] = useState(false);
  const [bulkShipResult, setBulkShipResult] = useState<BulkShipResponse | null>(null);
  const [bulkLabelsResult, setBulkLabelsResult] = useState<BulkLabelsResponse | null>(null);
  const [bulkLabelsLoading, setBulkLabelsLoading] = useState(false);

  // Fetch unfulfilled orders with auto-refresh
  // Pause auto-refresh when modals are open to avoid disrupting user actions
  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['fulfillmentOrders', searchValue, pageSize, cursor],
    queryFn: () => fetchFulfillmentOrders({
      status: 'unfulfilled',
      search: searchValue || undefined,
      limit: pageSize,
      cursor,
    }),
    refetchInterval: (showRateModal || showBulkShipModal) ? false : PENDING_ORDERS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // Reset pagination when search or page size changes
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    setCursor(undefined);
    setCursorHistory([]);
  }, []);

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSize(parseInt(value, 10));
    setCursor(undefined);
    setCursorHistory([]);
  }, []);

  const handleNextPage = useCallback(() => {
    if (data?.pageInfo?.endCursor) {
      setCursorHistory(prev => cursor ? [...prev, cursor] : prev);
      setCursor(data.pageInfo.endCursor);
    }
  }, [data?.pageInfo?.endCursor, cursor]);

  const handlePreviousPage = useCallback(() => {
    const prevCursor = cursorHistory[cursorHistory.length - 1];
    setCursorHistory(prev => prev.slice(0, -1));
    setCursor(cursorHistory.length > 1 ? prevCursor : undefined);
  }, [cursorHistory]);

  // Fetch rates for selected order
  const ratesQuery = useQuery({
    queryKey: ['rates', selectedOrder?.id],
    queryFn: () => selectedOrder ? fetchShippingRates(selectedOrder.id) : Promise.resolve(null),
    enabled: !!selectedOrder && showRateModal,
  });

  // Create shipment mutation
  const shipMutation = useMutation({
    mutationFn: ({ orderId, serviceLevel }: { orderId: string; serviceLevel: string }) =>
      createShipment(orderId, { serviceLevel }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fulfillmentOrders'] });
      // Don't close modal - show success state with label download
      setCreatedShipment(data);
    },
  });

  const handleSelectOrder = useCallback((order: OrderWithFulfillmentOrders) => {
    setSelectedOrder(order);
    setSelectedRate('');
    setCreatedShipment(null);
    setShowRateModal(true);
  }, []);

  const handleShip = useCallback(() => {
    if (!selectedOrder || !selectedRate) return;
    shipMutation.mutate({ orderId: selectedOrder.id, serviceLevel: selectedRate });
  }, [selectedOrder, selectedRate, shipMutation]);

  const handleCloseModal = useCallback(() => {
    setShowRateModal(false);
    setSelectedOrder(null);
    setSelectedRate('');
    setCreatedShipment(null);
    shipMutation.reset();
  }, [shipMutation]);

  const handleDownloadLabel = useCallback(async (format: 'PDF' | 'ZPL' | 'PNG' = 'PDF') => {
    if (!createdShipment?.shipment?.id) return;

    setLabelLoading(true);
    try {
      const { labelUrl } = await fetchLabelUrl(createdShipment.shipment.id, format);
      const trackingNumber = createdShipment.shipment.trackingCode || 'label';
      const ext = format.toLowerCase();
      const filename = `shipping-label-${trackingNumber}.${ext}`;

      // If it's a data URL (base64), trigger download; otherwise open in new tab
      if (labelUrl.startsWith('data:')) {
        downloadLabel(labelUrl, filename);
      } else {
        window.open(labelUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to fetch label:', err);
      alert('Failed to download label. Please try again.');
    } finally {
      setLabelLoading(false);
    }
  }, [createdShipment]);

  // Bulk selection handlers
  const allOrderIds = useMemo(() => data?.orders?.map(o => o.id) || [], [data?.orders]);
  const allSelected = allOrderIds.length > 0 && allOrderIds.every(id => selectedOrderIds.has(id));
  const someSelected = selectedOrderIds.size > 0;

  const handleToggleOrder = useCallback((orderId: string) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(allOrderIds));
    }
  }, [allSelected, allOrderIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedOrderIds(new Set());
  }, []);

  // Bulk ship mutation
  const bulkShipMutation = useMutation({
    mutationFn: () => {
      const orders = Array.from(selectedOrderIds).map(orderId => {
        const order = data?.orders?.find(o => o.id === orderId);
        const foId = order?.fulfillmentOrders?.nodes?.[0]?.id;
        return { orderId, fulfillmentOrderId: foId };
      });
      return bulkCreateShipments({ orders, serviceLevel: bulkServiceLevel });
    },
    onSuccess: (result) => {
      setBulkShipResult(result);
      queryClient.invalidateQueries({ queryKey: ['fulfillmentOrders'] });
      queryClient.invalidateQueries({ queryKey: ['fulfillmentCounts'] });
    },
  });

  const handleOpenBulkShipModal = useCallback(() => {
    setBulkShipResult(null);
    setBulkLabelsResult(null);
    setShowBulkShipModal(true);
  }, []);

  const handleCloseBulkShipModal = useCallback(() => {
    setShowBulkShipModal(false);
    setBulkShipResult(null);
    setBulkLabelsResult(null);
    bulkShipMutation.reset();
    // Clear selection after successful bulk ship
    if (bulkShipResult?.summary?.succeeded) {
      setSelectedOrderIds(new Set());
    }
  }, [bulkShipMutation, bulkShipResult]);

  const handleBulkShip = useCallback(() => {
    bulkShipMutation.mutate();
  }, [bulkShipMutation]);

  // Bulk download labels after successful bulk ship
  const handleBulkDownloadLabels = useCallback(async () => {
    if (!bulkShipResult?.results) return;

    const successfulShipments = bulkShipResult.results.filter(r => r.success && r.shipmentId);
    if (successfulShipments.length === 0) return;

    setBulkLabelsLoading(true);
    try {
      const result = await bulkFetchLabels({
        shipmentIds: successfulShipments.map(s => s.shipmentId!),
        format: 'PDF',
      });
      setBulkLabelsResult(result);

      // If merged PDF, download it directly
      if (result.merged && result.mergedLabelUrl) {
        const link = document.createElement('a');
        link.href = result.mergedLabelUrl;
        link.download = `shipping-labels-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (result.results) {
        // Fallback: open individual labels in new tabs
        for (const label of result.results.filter(r => r.success && r.labelUrl)) {
          window.open(label.labelUrl, '_blank');
        }
      }
    } catch (err) {
      console.error('Failed to fetch bulk labels:', err);
      alert('Failed to download labels. Please try again.');
    } finally {
      setBulkLabelsLoading(false);
    }
  }, [bulkShipResult]);

  const hasPreviousPage = cursorHistory.length > 0;
  const hasNextPage = data?.pageInfo?.hasNextPage || false;

  return (
    <div>
      {/* Search and Filters */}
      <div className="jitsu-card">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, maxWidth: '400px' }}>
            <TextField
              label="Search Orders"
              value={searchValue}
              onChange={handleSearch}
              autoComplete="off"
              placeholder="Order #, email, city, state, zip..."
            />
          </div>
          <div style={{ minWidth: '140px' }}>
            <Select
              label="Page Size"
              options={PAGE_SIZE_OPTIONS}
              value={String(pageSize)}
              onChange={handlePageSizeChange}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {dataUpdatedAt > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--jitsu-mid-grey)' }}>
                {formatLastUpdated(dataUpdatedAt)}
              </span>
            )}
            <Button onClick={() => refetch()} icon={<Icon source={RefreshIcon} />}>Refresh</Button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {someSelected && (
        <div className="jitsu-card" style={{ backgroundColor: 'var(--jitsu-light-grey)', borderLeft: '4px solid var(--jitsu-orange)' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>
              {selectedOrderIds.size} order{selectedOrderIds.size !== 1 ? 's' : ''} selected
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Select
                label=""
                labelHidden
                options={[
                  { label: 'Standard', value: 'STANDARD' },
                  { label: 'Next Day', value: 'NEXT_DAY' },
                  { label: 'Same Day', value: 'SAME_DAY' },
                ]}
                value={bulkServiceLevel}
                onChange={setBulkServiceLevel}
              />
              <Button variant="primary" onClick={handleOpenBulkShipModal}>
                Ship Selected
              </Button>
            </div>
            <Button variant="plain" onClick={handleClearSelection}>
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Orders List */}
      <div className="jitsu-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {data?.orders && data.orders.length > 0 && (
              <Checkbox
                label=""
                labelHidden
                checked={allSelected}
                onChange={handleSelectAll}
              />
            )}
            <h3 className="jitsu-card-title" style={{ margin: 0 }}>Awaiting Shipment</h3>
          </div>
          {data?.orders && <span style={{ color: 'var(--jitsu-mid-grey)', fontSize: '14px' }}>{data.orders.length} shown</span>}
        </div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner size="large" />
          </div>
        )}

        {error && (
          <Banner
            tone="critical"
            action={(error instanceof ApiError && error.needsAuth) ? {
              content: 'Authorize App',
              onAction: () => {
                const shop = getShopDomain() || new URLSearchParams(window.location.search).get('shop');
                if (shop) {
                  window.open(`/auth?shop=${encodeURIComponent(shop)}`, '_top');
                }
              },
            } : undefined}
          >
            {(error instanceof ApiError && error.needsAuth)
              ? 'Authorization required. Please authorize the app to access your Shopify store.'
              : `Failed to load orders: ${(error as Error).message}`
            }
          </Banner>
        )}

        {!isLoading && !error && data?.orders?.length === 0 && (
          <p style={{ color: 'var(--jitsu-mid-grey)', textAlign: 'center', padding: '40px' }}>
            No orders awaiting shipment.
          </p>
        )}

        {!isLoading && data?.orders && data.orders.length > 0 && (
          <div className="orders-list">
            {data.orders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                selected={selectedOrderIds.has(order.id)}
                onToggleSelect={() => handleToggleOrder(order.id)}
                onSelect={() => handleSelectOrder(order)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {(hasPreviousPage || hasNextPage) && (
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              hasPrevious={hasPreviousPage}
              hasNext={hasNextPage}
              onPrevious={handlePreviousPage}
              onNext={handleNextPage}
            />
          </div>
        )}
      </div>

      {/* Rate Shopping Modal */}
      <Modal
        open={showRateModal}
        onClose={handleCloseModal}
        title={createdShipment ? `Shipment Created - ${selectedOrder?.name || ''}` : `Ship Order ${selectedOrder?.name || ''}`}
        primaryAction={createdShipment ? {
          content: 'Done',
          onAction: handleCloseModal,
        } : {
          content: 'Create Shipment',
          onAction: handleShip,
          loading: shipMutation.isPending,
          disabled: !selectedRate,
        }}
        secondaryActions={createdShipment ? undefined : [{ content: 'Cancel', onAction: handleCloseModal }]}
      >
        <Modal.Section>
          {/* Show success state with label download */}
          {createdShipment ? (
            <div>
              <Banner tone="success">
                <p><strong>Shipment created successfully!</strong></p>
                <p style={{ marginTop: '8px' }}>
                  Tracking Number: <strong>{createdShipment.shipment?.trackingCode}</strong>
                </p>
              </Banner>

              <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>
                  Download Shipping Label
                </h4>
                <ButtonGroup>
                  <Button
                    onClick={() => handleDownloadLabel('PDF')}
                    loading={labelLoading}
                    icon={<Icon source={PrintIcon} />}
                  >
                    PDF Label
                  </Button>
                  <Button
                    onClick={() => handleDownloadLabel('ZPL')}
                    loading={labelLoading}
                    variant="secondary"
                  >
                    ZPL (Thermal)
                  </Button>
                  <Button
                    onClick={() => handleDownloadLabel('PNG')}
                    loading={labelLoading}
                    variant="secondary"
                  >
                    PNG Image
                  </Button>
                </ButtonGroup>
                <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--jitsu-mid-grey)' }}>
                  Click to download or print the shipping label for this order.
                </p>
              </div>

              {createdShipment.shipment?.trackingCode && (
                <div style={{ marginTop: '16px' }}>
                  <Button
                    url={`https://track.gojitsu.com/${createdShipment.shipment.trackingCode}`}
                    external
                    icon={<Icon source={ExternalIcon} />}
                    variant="plain"
                  >
                    Track Shipment
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <RateSelector
                rates={ratesQuery.data?.rates || []}
                loading={ratesQuery.isLoading}
                error={ratesQuery.error as Error | null}
                selectedRate={selectedRate}
                onSelectRate={setSelectedRate}
              />
              {shipMutation.isError && (
                <div style={{ marginTop: '16px' }}>
                  <Banner tone="critical">
                    Failed to create shipment: {(shipMutation.error as Error).message}
                  </Banner>
                </div>
              )}
            </>
          )}
        </Modal.Section>
      </Modal>

      {/* Bulk Ship Modal */}
      <Modal
        open={showBulkShipModal}
        onClose={handleCloseBulkShipModal}
        title={bulkShipResult ? 'Bulk Ship Complete' : `Ship ${selectedOrderIds.size} Orders`}
        primaryAction={bulkShipResult ? {
          content: 'Done',
          onAction: handleCloseBulkShipModal,
        } : {
          content: `Ship ${selectedOrderIds.size} Orders`,
          onAction: handleBulkShip,
          loading: bulkShipMutation.isPending,
        }}
        secondaryActions={bulkShipResult ? (bulkShipResult.summary.succeeded > 0 ? [{
          content: bulkLabelsLoading ? 'Downloading...' : 'Download All Labels',
          onAction: handleBulkDownloadLabels,
          disabled: bulkLabelsLoading,
        }] : undefined) : [{
          content: 'Cancel',
          onAction: handleCloseBulkShipModal,
        }]}
        size="large"
      >
        <Modal.Section>
          {bulkShipResult ? (
            <div>
              {/* Summary */}
              <div style={{ marginBottom: '20px' }}>
                <Banner tone={bulkShipResult.summary.failed === 0 ? 'success' : 'warning'}>
                  <p>
                    <strong>{bulkShipResult.summary.succeeded}</strong> of{' '}
                    <strong>{bulkShipResult.summary.total}</strong> shipments created successfully.
                    {bulkShipResult.summary.failed > 0 && (
                      <> <strong>{bulkShipResult.summary.failed}</strong> failed.</>
                    )}
                  </p>
                </Banner>
              </div>

              {/* Results list */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {bulkShipResult.results.map((result, idx) => {
                  const order = data?.orders?.find(o => o.id === result.orderId);
                  return (
                    <div
                      key={idx}
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 500 }}>{order?.name || result.orderId}</span>
                        {result.success && result.trackingCode && (
                          <span style={{ marginLeft: '12px', color: 'var(--jitsu-mid-grey)' }}>
                            {result.trackingCode}
                          </span>
                        )}
                      </div>
                      <Badge tone={result.success ? 'success' : 'critical'}>
                        {result.success ? 'Created' : result.error || 'Failed'}
                      </Badge>
                    </div>
                  );
                })}
              </div>

              {/* Labels download status */}
              {bulkLabelsResult && (
                <div style={{ marginTop: '16px' }}>
                  <Banner tone="success">
                    {bulkLabelsResult.merged
                      ? `Downloaded merged PDF with ${bulkLabelsResult.labelCount} label${bulkLabelsResult.labelCount !== 1 ? 's' : ''}.`
                      : `Opened ${bulkLabelsResult.summary.succeeded} label${bulkLabelsResult.summary.succeeded !== 1 ? 's' : ''} in new tabs.`
                    }
                  </Banner>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: '16px' }}>
                You are about to create shipments for <strong>{selectedOrderIds.size}</strong> orders
                using <strong>{bulkServiceLevel}</strong> service.
              </p>

              {bulkShipMutation.isPending && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ marginBottom: '8px', color: 'var(--jitsu-mid-grey)' }}>
                    Creating shipments...
                  </p>
                  <ProgressBar progress={50} size="small" />
                </div>
              )}

              {bulkShipMutation.isError && (
                <div style={{ marginTop: '16px' }}>
                  <Banner tone="critical">
                    Bulk ship failed: {(bulkShipMutation.error as Error).message}
                  </Banner>
                </div>
              )}

              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: 'var(--jitsu-mid-grey)', margin: 0 }}>
                  <strong>Note:</strong> Each order will be shipped with the selected service level.
                  Labels can be downloaded after all shipments are created.
                </p>
              </div>
            </div>
          )}
        </Modal.Section>
      </Modal>
    </div>
  );
}

// Order row component
interface OrderRowProps {
  order: OrderWithFulfillmentOrders;
  selected?: boolean;
  onToggleSelect?: () => void;
  onSelect: () => void;
}

function OrderRow({ order, selected, onToggleSelect, onSelect }: OrderRowProps) {
  const itemCount = order.fulfillmentOrders?.nodes?.reduce(
    (sum, fo) => sum + fo.lineItems.nodes.reduce((s, li) => s + li.remainingQuantity, 0),
    0
  ) || 0;

  const destination = order.shippingAddress;
  // Build recipient name
  const recipientName = [destination?.firstName, destination?.lastName].filter(Boolean).join(' ') || order.customer?.displayName || 'Guest';
  // Build location string
  const locationStr = [destination?.city, destination?.province, destination?.country]
    .filter(Boolean)
    .join(', ');
  // Build short address
  const shortAddress = destination?.address1 ? `${destination.address1}${destination.zip ? `, ${destination.zip}` : ''}` : '';

  const orderUrl = getShopifyOrderUrl(order.id);

  const handleOrderClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger row click
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger row click
  };

  return (
    <div
      className={`order-row ${selected ? 'order-row-selected' : ''}`}
      onClick={onSelect}
    >
      {onToggleSelect && (
        <div className="order-row-checkbox" onClick={handleCheckboxClick}>
          <Checkbox
            label=""
            labelHidden
            checked={selected || false}
            onChange={onToggleSelect}
          />
        </div>
      )}
      <div className="order-row-main">
        <div className="order-row-header">
          {orderUrl ? (
            <a
              href={orderUrl}
              target="_top"
              className="order-name order-link"
              onClick={handleOrderClick}
            >
              {order.name}
            </a>
          ) : (
            <span className="order-name">{order.name}</span>
          )}
          <Badge tone={order.displayFulfillmentStatus === 'UNFULFILLED' ? 'attention' : 'info'}>
            {formatShippingStatus(order.displayFulfillmentStatus)}
          </Badge>
        </div>
        <div className="order-row-details">
          <span><strong>{recipientName}</strong></span>
          {shortAddress && <span> • {shortAddress}</span>}
          {locationStr && !shortAddress && <span> • {locationStr}</span>}
        </div>
        <div className="order-row-details" style={{ fontSize: '12px', color: 'var(--jitsu-mid-grey)' }}>
          {shortAddress && locationStr && <span>{locationStr}</span>}
          <span>{shortAddress && locationStr ? ' • ' : ''}{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
          <span> • {new Date(order.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="order-row-action">
        <Button size="slim" variant="primary">Ship</Button>
      </div>
    </div>
  );
}

// Rate selector component
interface RateSelectorProps {
  rates: ShippingRate[];
  loading: boolean;
  error: Error | null;
  selectedRate: string;
  onSelectRate: (rate: string) => void;
}

function RateSelector({ rates, loading, error, selectedRate, onSelectRate }: RateSelectorProps) {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spinner size="small" />
        <p style={{ marginTop: '8px', color: 'var(--jitsu-mid-grey)' }}>
          Fetching shipping rates...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <Banner tone="critical">
        Failed to load rates: {error.message}
      </Banner>
    );
  }

  if (rates.length === 0) {
    return (
      <Banner tone="warning">
        No shipping rates available for this order.
      </Banner>
    );
  }

  const options = rates.map((rate) => ({
    label: `${rate.serviceName} - ${rate.currency} ${rate.price.toFixed(2)}${
      rate.estimatedDays ? ` (${rate.estimatedDays} days)` : ''
    }`,
    value: rate.serviceLevel,
  }));

  return (
    <div>
      <Select
        label="Select Shipping Service"
        options={[{ label: 'Choose a service...', value: '' }, ...options]}
        value={selectedRate}
        onChange={onSelectRate}
      />
      {selectedRate && (
        <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          {rates.filter(r => r.serviceLevel === selectedRate).map((rate) => (
            <div key={rate.serviceLevel}>
              <strong>{rate.serviceName}</strong>
              <p style={{ margin: '4px 0', color: 'var(--jitsu-mid-grey)' }}>
                Price: {rate.currency} {rate.price.toFixed(2)}
              </p>
              {rate.estimatedDays && (
                <p style={{ margin: '4px 0', color: 'var(--jitsu-mid-grey)' }}>
                  Estimated delivery: {rate.estimatedDays} business days
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// SHIPPED ORDERS PANEL
// ============================================
const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Label Created', value: 'label_created' },
  { label: 'Picked Up', value: 'picked_up' },
  { label: 'In Transit', value: 'in_transit' },
  { label: 'Out for Delivery', value: 'out_for_delivery' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Attempted Delivery', value: 'attempted_delivery' },
  { label: 'Failed', value: 'failed' },
  { label: 'Exception', value: 'exception' },
  { label: 'Cancelled', value: 'cancelled' },
];

const DATE_RANGE_OPTIONS = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 14 days', value: '14' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Specific date', value: 'specific' },
  { label: 'All time', value: '' },
];

// Helper to get date N days ago in YYYY-MM-DD format
function getDateDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

// Helper to get today's date in YYYY-MM-DD format
function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

function ShippedOrdersPanel() {
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceLevelFilter, setServiceLevelFilter] = useState('');
  const [dateRange, setDateRange] = useState('7'); // Default to 7 days
  const [specificDate, setSpecificDate] = useState(getTodayDate());
  const [deliveryDateFilter, setDeliveryDateFilter] = useState(''); // Estimated delivery date filter
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentListItem | null>(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);

  // Bulk selection state
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<Set<string>>(new Set());
  const [bulkLabelsLoading, setBulkLabelsLoading] = useState(false);

  // Export state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Calculate start/end dates based on date range selection
  const getDateFilters = useCallback(() => {
    if (dateRange === 'specific') {
      return { startDate: specificDate, endDate: specificDate };
    } else if (dateRange) {
      return { startDate: getDateDaysAgo(parseInt(dateRange, 10)), endDate: undefined };
    }
    return { startDate: undefined, endDate: undefined };
  }, [dateRange, specificDate]);

  const { startDate, endDate } = getDateFilters();

  // Auto-refresh shipped orders, pause when tracking modal is open
  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['shipments', statusFilter, serviceLevelFilter, page, pageSize, startDate, endDate, debouncedSearch, deliveryDateFilter],
    queryFn: () => fetchShipments({
      status: statusFilter || undefined,
      serviceLevel: serviceLevelFilter || undefined,
      page,
      limit: pageSize,
      startDate,
      endDate,
      search: debouncedSearch || undefined,
      deliveryDate: deliveryDateFilter || undefined,
    }),
    refetchInterval: showTrackingModal ? false : SHIPPED_ORDERS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handleServiceLevelChange = useCallback((value: string) => {
    setServiceLevelFilter(value);
    setPage(1);
  }, []);

  const handleDeliveryDateChange = useCallback((value: string) => {
    setDeliveryDateFilter(value);
    setPage(1);
  }, []);

  const handleDateRangeChange = useCallback((value: string) => {
    setDateRange(value);
    setPage(1);
  }, []);

  const handleSpecificDateChange = useCallback((value: string) => {
    setSpecificDate(value);
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSize(parseInt(value, 10));
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleViewTracking = useCallback((shipment: ShipmentListItem) => {
    setSelectedShipment(shipment);
    setShowTrackingModal(true);
  }, []);

  // Export CSV handler
  const handleExportCsv = useCallback(async () => {
    setExportLoading(true);
    setExportError(null);
    try {
      await exportShipmentsCsv({
        status: statusFilter || undefined,
        serviceLevel: serviceLevelFilter || undefined,
        startDate,
        endDate,
        search: debouncedSearch || undefined,
        deliveryDate: deliveryDateFilter || undefined,
      });
    } catch (err) {
      console.error('Failed to export CSV:', err);
      setExportError(err instanceof Error ? err.message : 'Failed to export CSV. Please try again.');
    } finally {
      setExportLoading(false);
    }
  }, [statusFilter, serviceLevelFilter, startDate, endDate, debouncedSearch, deliveryDateFilter]);

  // Cancel shipment state
  const [cancellingShipmentId, setCancellingShipmentId] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [shipmentToCancel, setShipmentToCancel] = useState<ShipmentListItem | null>(null);
  const [bulkCancelLoading, setBulkCancelLoading] = useState(false);
  const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);

  // Open cancel confirmation modal for single shipment
  const handleCancelClick = useCallback((shipment: ShipmentListItem) => {
    setShipmentToCancel(shipment);
    setShowCancelModal(true);
  }, []);

  // Confirm single cancel
  const handleConfirmCancel = useCallback(async () => {
    if (!shipmentToCancel) return;

    setCancellingShipmentId(shipmentToCancel.id);
    setShowCancelModal(false);
    try {
      await cancelShipment(shipmentToCancel.id);
      refetch();
    } catch (err) {
      console.error('Failed to cancel shipment:', err);
      // Error will be shown via toast or banner in future
    } finally {
      setCancellingShipmentId(null);
      setShipmentToCancel(null);
    }
  }, [shipmentToCancel, refetch]);

  // Bulk cancel handler
  const handleBulkCancelClick = useCallback(() => {
    setShowBulkCancelModal(true);
  }, []);

  const handleConfirmBulkCancel = useCallback(async () => {
    if (selectedShipmentIds.size === 0) return;

    // Get cancellable shipments (not already cancelled or delivered)
    const cancellableShipments = data?.shipments?.filter(
      s => selectedShipmentIds.has(s.id) && s.status !== 'cancelled' && s.status !== 'delivered'
    ) || [];

    if (cancellableShipments.length === 0) {
      setShowBulkCancelModal(false);
      return;
    }

    setBulkCancelLoading(true);
    setShowBulkCancelModal(false);

    let successCount = 0;
    let failCount = 0;

    for (const shipment of cancellableShipments) {
      try {
        await cancelShipment(shipment.id);
        successCount++;
      } catch (err) {
        console.error(`Failed to cancel shipment ${shipment.id}:`, err);
        failCount++;
      }
    }

    setBulkCancelLoading(false);
    setSelectedShipmentIds(new Set());
    refetch();

    // Could show a toast here with results
    console.log(`Bulk cancel complete: ${successCount} succeeded, ${failCount} failed`);
  }, [selectedShipmentIds, data?.shipments, refetch]);

  // Bulk selection handlers
  const allShipmentIds = useMemo(() =>
    data?.shipments?.filter(s => s.jitsuShipmentId).map(s => s.id) || [],
    [data?.shipments]
  );
  const allSelected = allShipmentIds.length > 0 && allShipmentIds.every(id => selectedShipmentIds.has(id));
  const someSelected = selectedShipmentIds.size > 0;

  const handleToggleShipment = useCallback((shipmentId: string) => {
    setSelectedShipmentIds(prev => {
      const next = new Set(prev);
      if (next.has(shipmentId)) {
        next.delete(shipmentId);
      } else {
        next.add(shipmentId);
      }
      return next;
    });
  }, []);

  const handleSelectAllShipments = useCallback(() => {
    if (allSelected) {
      setSelectedShipmentIds(new Set());
    } else {
      setSelectedShipmentIds(new Set(allShipmentIds));
    }
  }, [allSelected, allShipmentIds]);

  const handleClearShipmentSelection = useCallback(() => {
    setSelectedShipmentIds(new Set());
  }, []);

  const handleBulkDownloadLabels = useCallback(async () => {
    if (selectedShipmentIds.size === 0) return;

    // Get the jitsuShipmentIds for selected shipments
    const selectedShipments = data?.shipments?.filter(s => selectedShipmentIds.has(s.id) && s.jitsuShipmentId) || [];
    if (selectedShipments.length === 0) return;

    setBulkLabelsLoading(true);
    try {
      const result = await bulkFetchLabels({
        shipmentIds: selectedShipments.map(s => s.jitsuShipmentId!),
        format: 'PDF',
      });

      // If merged PDF, download it directly
      if (result.merged && result.mergedLabelUrl) {
        const link = document.createElement('a');
        link.href = result.mergedLabelUrl;
        link.download = `shipping-labels-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (result.results) {
        // Fallback: open individual labels in new tabs
        for (const label of result.results.filter(r => r.success && r.labelUrl)) {
          window.open(label.labelUrl, '_blank');
        }
      }

      if (result.summary.failed > 0) {
        alert(`Downloaded ${result.summary.succeeded} labels. ${result.summary.failed} failed.`);
      }
    } catch (err) {
      console.error('Failed to fetch bulk labels:', err);
      alert('Failed to download labels. Please try again.');
    } finally {
      setBulkLabelsLoading(false);
    }
  }, [selectedShipmentIds, data?.shipments]);

  // Service level filter options
  const SERVICE_LEVEL_OPTIONS = [
    { label: 'All Service Levels', value: '' },
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Next Day', value: 'NEXT_DAY' },
    { label: 'Same Day', value: 'SAME_DAY' },
  ];

  return (
    <>
      {/* Filters */}
      <div className="jitsu-card">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '160px' }}>
            <Select
              label="Date Range"
              options={DATE_RANGE_OPTIONS}
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </div>
          {dateRange === 'specific' && (
            <div style={{ minWidth: '160px' }}>
              <TextField
                label="Date"
                type="date"
                value={specificDate}
                onChange={handleSpecificDateChange}
                autoComplete="off"
              />
            </div>
          )}
          <div style={{ minWidth: '180px' }}>
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={handleStatusChange}
            />
          </div>
          <div style={{ minWidth: '140px' }}>
            <Select
              label="Service Level"
              options={SERVICE_LEVEL_OPTIONS}
              value={serviceLevelFilter}
              onChange={handleServiceLevelChange}
            />
          </div>
          <div style={{ minWidth: '150px' }}>
            <TextField
              label="Est. Delivery"
              type="date"
              value={deliveryDateFilter}
              onChange={handleDeliveryDateChange}
              autoComplete="off"
              placeholder="Filter by delivery date"
              clearButton
              onClearButtonClick={() => handleDeliveryDateChange('')}
            />
          </div>
          <div style={{ minWidth: '100px' }}>
            <Select
              label="Per Page"
              options={PAGE_SIZE_OPTIONS}
              value={String(pageSize)}
              onChange={handlePageSizeChange}
            />
          </div>
          <div style={{ flex: 1, maxWidth: '280px' }}>
            <TextField
              label="Search"
              value={searchValue}
              onChange={handleSearchChange}
              autoComplete="off"
              placeholder="Order, tracking, recipient, city..."
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {dataUpdatedAt > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--jitsu-mid-grey)' }}>
                {formatLastUpdated(dataUpdatedAt)}
              </span>
            )}
            <Button onClick={() => refetch()} icon={<Icon source={RefreshIcon} />}>
              Refresh
            </Button>
            <Button
              onClick={handleExportCsv}
              loading={exportLoading}
              icon={<Icon source={ExportIcon} />}
            >
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar for Shipments */}
      {someSelected && (
        <div className="jitsu-card" style={{ backgroundColor: 'var(--jitsu-light-grey)', borderLeft: '4px solid var(--jitsu-azure)' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>
              {selectedShipmentIds.size} shipment{selectedShipmentIds.size !== 1 ? 's' : ''} selected
            </span>
            <Button
              variant="primary"
              onClick={handleBulkDownloadLabels}
              loading={bulkLabelsLoading}
              icon={<Icon source={PrintIcon} />}
            >
              Download Labels
            </Button>
            <Button
              tone="critical"
              onClick={handleBulkCancelClick}
              loading={bulkCancelLoading}
              icon={<Icon source={XCircleIcon} />}
            >
              Cancel Shipments
            </Button>
            <Button variant="plain" onClick={handleClearShipmentSelection}>
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Shipments List */}
      <div className="jitsu-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {data?.shipments && data.shipments.length > 0 && (
              <Checkbox
                label=""
                labelHidden
                checked={allSelected}
                onChange={handleSelectAllShipments}
              />
            )}
            <h3 className="jitsu-card-title" style={{ margin: 0 }}>Shipments</h3>
          </div>
          {data && (
            <span style={{ color: 'var(--jitsu-mid-grey)', fontSize: '14px' }}>
              {data.total} total • Page {data.page} of {data.totalPages}
            </span>
          )}
        </div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner size="large" />
          </div>
        )}

        {error && (
          <Banner
            tone="critical"
            action={(error instanceof ApiError && error.needsAuth) ? {
              content: 'Authorize App',
              onAction: () => {
                const shop = getShopDomain() || new URLSearchParams(window.location.search).get('shop');
                if (shop) {
                  window.open(`/auth?shop=${encodeURIComponent(shop)}`, '_top');
                }
              },
            } : undefined}
          >
            {(error instanceof ApiError && error.needsAuth)
              ? 'Authorization required. Please authorize the app to access your Shopify store.'
              : `Failed to load shipments: ${(error as Error).message}`
            }
          </Banner>
        )}

        {exportError && (
          <Banner tone="critical" onDismiss={() => setExportError(null)}>
            {exportError}
          </Banner>
        )}

        {!isLoading && !error && data?.shipments?.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <img src="/images/jitsu-comet.gif" alt="" style={{ height: '60px', marginBottom: '16px', opacity: 0.6 }} />
            <p style={{ color: 'var(--jitsu-mid-grey)' }}>No shipments found.</p>
          </div>
        )}

        {!isLoading && data?.shipments && data.shipments.length > 0 && (
          <div className="shipments-list">
            {data.shipments.map((shipment) => (
              <ShipmentRow
                key={shipment.id}
                shipment={shipment}
                selected={selectedShipmentIds.has(shipment.id)}
                onToggleSelect={() => handleToggleShipment(shipment.id)}
                onViewTracking={() => handleViewTracking(shipment)}
                onCancel={() => handleCancelClick(shipment)}
                cancelLoading={cancellingShipmentId === shipment.id}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              hasPrevious={page > 1}
              hasNext={page < data.totalPages}
              onPrevious={() => setPage(p => p - 1)}
              onNext={() => setPage(p => p + 1)}
            />
          </div>
        )}
      </div>

      {/* Tracking Modal */}
      <Modal
        open={showTrackingModal}
        onClose={() => setShowTrackingModal(false)}
        title={`Tracking - ${selectedShipment?.trackingNumbers?.[0] || 'Shipment'}`}
        primaryAction={{ content: 'Close', onAction: () => setShowTrackingModal(false) }}
      >
        <Modal.Section>
          {selectedShipment && <TrackingTimeline shipment={selectedShipment} />}
        </Modal.Section>
      </Modal>

      {/* Single Cancel Confirmation Modal */}
      <Modal
        open={showCancelModal}
        onClose={() => { setShowCancelModal(false); setShipmentToCancel(null); }}
        title="Cancel Shipment"
        primaryAction={{
          content: 'Cancel Shipment',
          onAction: handleConfirmCancel,
          destructive: true,
        }}
        secondaryActions={[{
          content: 'Keep Shipment',
          onAction: () => { setShowCancelModal(false); setShipmentToCancel(null); },
        }]}
      >
        <Modal.Section>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <img src="/images/jitsu-comet.gif" alt="" style={{ height: '48px', marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              Are you sure you want to cancel this shipment?
            </p>
            {shipmentToCancel && (
              <p style={{ color: 'var(--jitsu-mid-grey)' }}>
                Order: <strong>{shipmentToCancel.orderName || shipmentToCancel.orderId}</strong>
                {shipmentToCancel.trackingNumbers?.[0] && (
                  <> • Tracking: <strong>{shipmentToCancel.trackingNumbers[0]}</strong></>
                )}
              </p>
            )}
            <p style={{ color: 'var(--jitsu-mid-grey)', fontSize: '14px', marginTop: '12px' }}>
              This will attempt to cancel the shipment with the carrier. Refunds may apply based on carrier policy.
            </p>
          </div>
        </Modal.Section>
      </Modal>

      {/* Bulk Cancel Confirmation Modal */}
      <Modal
        open={showBulkCancelModal}
        onClose={() => setShowBulkCancelModal(false)}
        title="Cancel Multiple Shipments"
        primaryAction={{
          content: `Cancel ${data?.shipments?.filter(s => selectedShipmentIds.has(s.id) && s.status !== 'cancelled' && s.status !== 'delivered').length || 0} Shipments`,
          onAction: handleConfirmBulkCancel,
          destructive: true,
        }}
        secondaryActions={[{
          content: 'Keep Shipments',
          onAction: () => setShowBulkCancelModal(false),
        }]}
      >
        <Modal.Section>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <img src="/images/jitsu-comet.gif" alt="" style={{ height: '48px', marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              Are you sure you want to cancel {data?.shipments?.filter(s => selectedShipmentIds.has(s.id) && s.status !== 'cancelled' && s.status !== 'delivered').length || 0} shipments?
            </p>
            <p style={{ color: 'var(--jitsu-mid-grey)', fontSize: '14px', marginTop: '12px' }}>
              This will attempt to cancel all selected shipments with their carriers. Refunds may apply based on carrier policies.
            </p>
          </div>
        </Modal.Section>
      </Modal>
    </>
  );
}

// Shipment row component
interface ShipmentRowProps {
  shipment: ShipmentListItem;
  selected?: boolean;
  onToggleSelect?: () => void;
  onViewTracking: () => void;
  onCancel?: () => void;
  cancelLoading?: boolean;
}

function ShipmentRow({ shipment, selected, onToggleSelect, onViewTracking, onCancel, cancelLoading }: ShipmentRowProps) {
  const [labelLoading, setLabelLoading] = useState(false);

  // Can cancel if not already cancelled or delivered
  const canCancel = shipment.status !== 'cancelled' && shipment.status !== 'delivered';

  const handleDownloadLabel = async (format: 'PDF' | 'ZPL' | 'PNG' = 'PDF') => {
    if (!shipment.jitsuShipmentId) return;
    setLabelLoading(true);
    try {
      const { labelUrl } = await fetchLabelUrl(shipment.jitsuShipmentId, format);
      const trackingNum = shipment.trackingNumbers?.[0] || 'label';
      const ext = format.toLowerCase();
      const filename = `shipping-label-${trackingNum}.${ext}`;

      // If it's a data URL (base64), trigger download; otherwise open in new tab
      if (labelUrl.startsWith('data:')) {
        downloadLabel(labelUrl, filename);
      } else {
        window.open(labelUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to fetch label:', err);
      alert('Failed to download label');
    } finally {
      setLabelLoading(false);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const statusTone = getStatusTone(shipment.status);
  const trackingNumber = shipment.trackingNumbers?.[0];
  const orderUrl = getShopifyOrderUrl(shipment.orderId);
  const orderNumber = shipment.orderName || shipment.orderId.split('/').pop();

  // Build recipient location string
  const recipientLocation = [shipment.recipientCity, shipment.recipientState, shipment.recipientZip]
    .filter(Boolean)
    .join(', ');

  // Format estimated delivery date
  const estimatedDelivery = shipment.estimatedDeliveryDate
    ? new Date(shipment.estimatedDeliveryDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : null;

  return (
    <div className={`shipment-row ${selected ? 'shipment-row-selected' : ''}`}>
      {onToggleSelect && shipment.jitsuShipmentId && (
        <div className="shipment-row-checkbox" onClick={handleCheckboxClick}>
          <Checkbox
            label=""
            labelHidden
            checked={selected || false}
            onChange={onToggleSelect}
          />
        </div>
      )}
      <div className="shipment-row-main">
        <div className="shipment-row-header">
          <span className="shipment-tracking">{trackingNumber || 'No tracking'}</span>
          <Badge tone={statusTone}>{formatStatus(shipment.status)}</Badge>
          {shipment.serviceLevel && (
            <Badge tone="info">{shipment.serviceLevel.replace('_', ' ')}</Badge>
          )}
        </div>
        <div className="shipment-row-details">
          <span>Order: {orderUrl ? (
            <a href={orderUrl} target="_top" className="order-link">{orderNumber}</a>
          ) : (
            orderNumber
          )}</span>
          {shipment.recipientName && <span> • <strong>{shipment.recipientName}</strong></span>}
          {recipientLocation && <span> • {recipientLocation}</span>}
        </div>
        <div className="shipment-row-details" style={{ fontSize: '12px', color: 'var(--jitsu-mid-grey)' }}>
          <span>Created: {new Date(shipment.createdAt).toLocaleDateString()}</span>
          {estimatedDelivery && <span> • Est. delivery: <strong>{estimatedDelivery}</strong></span>}
          {shipment.carrierCompany && <span> • {shipment.carrierCompany}</span>}
        </div>
      </div>
      <div className="shipment-row-actions">
        <ButtonGroup>
          <Button size="slim" onClick={onViewTracking} icon={<Icon source={ViewIcon} />}>
            Track
          </Button>
          {shipment.jitsuShipmentId && (
            <Button
              size="slim"
              onClick={() => handleDownloadLabel('PDF')}
              loading={labelLoading}
              icon={<Icon source={PrintIcon} />}
            >
              Label
            </Button>
          )}
          {trackingNumber && (
            <Button
              size="slim"
              url={`https://track.gojitsu.com/${trackingNumber}`}
              external
              icon={<Icon source={ExternalIcon} />}
            >
              Jitsu
            </Button>
          )}
          {canCancel && onCancel && (
            <Button
              size="slim"
              tone="critical"
              onClick={onCancel}
              loading={cancelLoading}
              icon={<Icon source={XCircleIcon} />}
            >
              Cancel
            </Button>
          )}
        </ButtonGroup>
      </div>
    </div>
  );
}

// Tracking timeline component
interface TrackingTimelineProps {
  shipment: ShipmentListItem;
}

function TrackingTimeline({ shipment }: TrackingTimelineProps) {
  const trackingNumber = shipment.trackingNumbers?.[0];

  // Define the tracking stages
  const stages = [
    { key: 'label_created', label: 'Label Created', icon: '📋' },
    { key: 'picked_up', label: 'Picked Up', icon: '📦' },
    { key: 'in_transit', label: 'In Transit', icon: '🚚' },
    { key: 'out_for_delivery', label: 'Out for Delivery', icon: '🏃' },
    { key: 'delivered', label: 'Delivered', icon: '✅' },
  ];

  // Find current stage index
  const statusMap: Record<string, number> = {
    'pending': 0,
    'label_created': 0,
    'fulfilled': 0,
    'picked_up': 1,
    'at_depot': 2,
    'in_transit': 2,
    'out_for_delivery': 3,
    'delivered': 4,
  };
  const currentStageIndex = statusMap[shipment.status] ?? -1;
  const isException = ['delivery_failed', 'cancelled', 'exception', 'returned'].includes(shipment.status);

  return (
    <div>
      {/* Current Status */}
      <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: isException ? '#FEF3F2' : '#F0FDF4', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Badge tone={getStatusTone(shipment.status)}>{formatStatus(shipment.status)}</Badge>
          {trackingNumber && (
            <span style={{ fontFamily: 'monospace', color: 'var(--jitsu-mid-grey)' }}>{trackingNumber}</span>
          )}
        </div>
        <p style={{ margin: '8px 0 0', fontSize: '14px', color: 'var(--jitsu-grey)' }}>
          Last updated: {new Date(shipment.updatedAt).toLocaleString()}
        </p>
      </div>

      {/* Timeline */}
      <div className="tracking-timeline">
        {stages.map((stage, index) => {
          const isCompleted = index <= currentStageIndex && !isException;
          const isCurrent = index === currentStageIndex && !isException;

          return (
            <div key={stage.key} className={`timeline-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
              <div className="timeline-marker">
                <span className="timeline-icon">{stage.icon}</span>
                {index < stages.length - 1 && <div className="timeline-line" />}
              </div>
              <div className="timeline-content">
                <span className="timeline-label">{stage.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Exception message */}
      {isException && (
        <Banner tone="critical">
          <p>Shipment status: {formatStatus(shipment.status)}</p>
        </Banner>
      )}

      {/* External tracking link */}
      {trackingNumber && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Button
            url={`https://track.gojitsu.com/${trackingNumber}`}
            external
            icon={<Icon source={ExternalIcon} />}
          >
            View Full Tracking on Jitsu
          </Button>
        </div>
      )}
    </div>
  );
}
